import React, { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { SITE, getContact } from "@/lib/site";
import { useContent } from "@/lib/content";

const links = [
  { label: "Home", href: "/#hero", testid: "nav-home-link" },
  { label: "Courses", href: "/#courses", testid: "nav-courses-link" },
  { label: "Faculty", href: "/faculty", testid: "nav-faculty-link" },
  { label: "Toppers", href: "/toppers", testid: "nav-toppers-link" },
  { label: "Career Guidance", href: "/#guidance", testid: "nav-guidance-link" },
  { label: "Fees & EMI", href: "/#fees", testid: "nav-fees-link" },
  { label: "Contact", href: "/#contact", testid: "nav-contact-link" },
];

export default function Header({ onConsult }) {
  const { content } = useContent();
  const c = getContact(content);
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm"
          : "bg-white/80 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-[72px] md:h-[84px] flex items-center justify-between">
        <a href="#hero" className="flex items-center gap-3" data-testid="header-logo-link">
          <img
            src={SITE.logo.horizontal}
            alt={SITE.name}
            className="h-9 md:h-11 w-auto object-contain"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              className="text-sm font-inter font-medium text-slate-600 hover:text-[#002570] transition-colors"
            >
              {l.label}
            </a>
          ))}
          <a
            href="/student/login"
            data-testid="nav-student-login-link"
            className="text-sm font-inter font-semibold text-[#002570] hover:text-[#001a52] transition-colors border-l border-slate-200 pl-6"
          >
            Student Login
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${c.phoneRaw}`}
            data-testid="header-call-link"
            className="hidden sm:flex items-center gap-2 text-sm font-medium text-[#002570] hover:text-[#001a52]"
          >
            <Phone className="w-4 h-4" />
            <span>{c.phone}</span>
          </a>
          <button
            onClick={onConsult}
            data-testid="header-book-consultation-btn"
            className="hidden md:inline-flex items-center gap-2 bg-[#002570] text-white hover:bg-[#001a52] rounded-full px-5 py-2.5 text-sm font-medium transition-all hover:shadow-lg"
          >
            Book Free Consultation
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="header-mobile-menu-btn"
            aria-label="Toggle menu"
            className="lg:hidden p-2 rounded-md text-[#002570] hover:bg-slate-100"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-5 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="py-3 px-2 text-base font-inter font-medium text-slate-700 hover:text-[#002570] hover:bg-slate-50 rounded-md"
              >
                {l.label}
              </a>
            ))}
            <a
              href="/student/login"
              onClick={() => setOpen(false)}
              data-testid="mobile-student-login-link"
              className="py-3 px-2 text-base font-inter font-semibold text-[#002570] hover:bg-slate-50 rounded-md border-t border-slate-200 mt-1 pt-3"
            >
              Student Login
            </a>
            <button
              onClick={() => {
                setOpen(false);
                onConsult();
              }}
              data-testid="mobile-book-consultation-btn"
              className="mt-2 bg-[#002570] text-white rounded-full px-5 py-3 text-sm font-medium"
            >
              Book Free Consultation
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
